import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import { COLUMN_DEFINITIONS, type FieldError } from '@/lib/excel';
import { useImportPartnerContacts } from '@/pages/partner/api/mutations/usePartnerContacts';
import { toastApiError } from '@/api/mutations/toast-api-error';
import type { ContactImportPreview } from './parsePartnerContacts';

interface ContactImportPreviewDialogProps {
  preview: ContactImportPreview | null;
  onClose: () => void;
}

/** "field: message" as the sheet's column and a readable reason. */
function useRowError() {
  const { t } = useTranslation();
  return ({ field, message }: FieldError) => {
    const column = COLUMN_DEFINITIONS.partnerContacts.find((entry) => entry.field === field);
    const label = column ? t(column.translationKey).replace('*', '').trim() : field;
    return label ? `${label}: ${t(message)}` : t(message);
  };
}

/**
 * The second step of the import: says what will be added, what updated and
 * which rows the sheet still has to fix, before anything is written.
 */
export function ContactImportPreviewDialog({ preview, onClose }: ContactImportPreviewDialogProps) {
  const { t } = useTranslation();
  const importContacts = useImportPartnerContacts();
  const rowError = useRowError();

  const confirm = () => {
    if (!preview) return;
    importContacts.mutate(
      { contacts: preview.rows.map((row) => row.data) },
      {
        onSuccess: ({ created, updated }) => {
          toast.success(t('partner.contacts.import.done', { created, updated }));
          onClose();
        },
        onError: (error) => toastApiError(error, 'common.Error')
      }
    );
  };

  return (
    <Dialog open={preview !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('partner.contacts.import.previewTitle')}</DialogTitle>
          <DialogDescription>
            {preview && preview.rows.length > 0
              ? t('partner.contacts.import.summary', {
                  count: preview.rows.length,
                  created: preview.created,
                  updated: preview.updated
                })
              : t('partner.contacts.import.nothingToImport')}
          </DialogDescription>
        </DialogHeader>

        {preview && preview.invalid.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="flex items-center gap-2 text-sm font-medium text-warning">
              <AlertTriangle className="h-4 w-4" />
              {t('partner.contacts.import.skipped', { count: preview.invalid.length })}
            </p>
            <ul className="max-h-60 overflow-y-auto rounded-md border text-sm">
              {preview.invalid.map((row) => (
                <li key={row.rowNumber} className="border-b px-3 py-2 last:border-b-0">
                  <span className="font-medium">
                    {t('partner.contacts.import.row', { row: row.rowNumber })}
                  </span>
                  <span className="text-muted-foreground">
                    {' · '}
                    {(row.errors ?? []).map(rowError).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirm}
            loading={importContacts.isPending}
            disabled={importContacts.isPending || !preview?.rows.length}
          >
            {t('partner.contacts.import.confirm', { count: preview?.rows.length ?? 0 })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
